import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import i18n from "i18next";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import { formatDateTime, getPatientName, buildInteractionParam } from "../pages/utils";
import { ddiRelevances, phenotypeTypes } from "../../resources/json/jsons";
import { Consts } from "../pages/consts";
import { Tooltip } from "@mui/material";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import useDialogDrag from "../../hooks/useDialogDrag";

const ALL_CATEGORY_IDS = [
  "diseases",
  "pgx",
  "pgxRecommendations",
  "drugs",
  "symptoms",
  "labTests",
  "measurements",
];

const PDF_LAYOUT = {
  top: 30,
  left: 14,
  right: 14,
  bottom: 12,
  sectionGap: 7,
};

const getPdfContentWidth = (doc) => doc.internal.pageSize.width - PDF_LAYOUT.left - PDF_LAYOUT.right;

const PDF_FONT = {
  family: "NotoSans",
  regularFile: "NotoSans-Regular.ttf",
  boldFile: "NotoSans-Bold.ttf",
  // Prefer local static assets first; fallback to CDN for environments without bundled font files.
  regularSources: [
    "/resources/fonts/NotoSans-Regular.ttf",
    "https://raw.githubusercontent.com/googlefonts/noto-fonts/main/hinted/ttf/NotoSans/NotoSans-Regular.ttf",
  ],
  boldSources: [
    "/resources/fonts/NotoSans-Bold.ttf",
    "https://raw.githubusercontent.com/googlefonts/noto-fonts/main/hinted/ttf/NotoSans/NotoSans-Bold.ttf",
  ],
};

let cachedPdfFontData = null;

const chunkedArrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const fetchPdfFontAsBase64 = async (url) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Unable to load font: ${url}`);
  }

  return chunkedArrayBufferToBase64(await response.arrayBuffer());
};

const fetchFirstAvailableFontSource = async (sources = []) => {
  for (const source of sources) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fetchPdfFontAsBase64(source);
    } catch (_error) {
      // Try next source.
    }
  }

  throw new Error("Unable to load any configured PDF font source");
};

const loadPdfFontData = async () => {
  if (cachedPdfFontData) {
    return cachedPdfFontData;
  }

  const [regular, bold] = await Promise.all([
    fetchFirstAvailableFontSource(PDF_FONT.regularSources),
    fetchFirstAvailableFontSource(PDF_FONT.boldSources),
  ]);

  cachedPdfFontData = { regular, bold };
  return cachedPdfFontData;
};

const applyPdfUnicodeFont = async (doc) => {
  try {
    const fontData = await loadPdfFontData();
    doc.addFileToVFS(PDF_FONT.regularFile, fontData.regular);
    doc.addFont(PDF_FONT.regularFile, PDF_FONT.family, "normal");
    doc.addFileToVFS(PDF_FONT.boldFile, fontData.bold);
    doc.addFont(PDF_FONT.boldFile, PDF_FONT.family, "bold");
    return PDF_FONT.family;
  } catch (_error) {
    return "helvetica";
  }
};

const ReportPrint = ({ selectedPatient = null, onFinishPrintReport = null }) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);

  const {
    getDbDrugs,
    getDbUser,
    getDbDiseases,
    getDbGenes,
    getDbSymptoms,
    getDbLabTests,
    getDbMeasurements,
    getDosageRecommendationReport,
  } = useMmcService();

  const [isPrinting, setIsPrinting] = useState(false);
  const patientId = selectedPatient ? selectedPatient?.id : context?.patient?.id;
  const disabledSeeNames = context?.role === Consts.STUDY_DIRECTOR || context?.role === "admin";
  const [selectedItems, setSelectedItems] = useState(ALL_CATEGORY_IDS);
  const [showSelection, setShowSelection] = useState(false);

  const { dragStyle, isDragging, onDragHandleMouseDown } = useDialogDrag(showSelection, {
    baseTransform: "translate(-50%, -50%)",
  });

  useEffect(() => {
    if (selectedPatient?.id) {
      setShowSelection(true);
    }
  }, [selectedPatient?.id]);

  const closeSelectionDialog = () => {
    setShowSelection(false);

    // In patient-list print flow, closing the dialog should release the row action state.
    if (selectedPatient && onFinishPrintReport) {
      onFinishPrintReport();
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return `${value}`;
  };

  const normalizeSubstance = (value) => {
    if (!value) {
      return "-";
    }

    return `${value}`.split("&&&").join(", ");
  };

  const getRiskSubstance = (risk) => {
    if (!risk) {
      return "-";
    }

    try {
      if (risk?.recommendation?.includes("EN")) {
        const parsed = JSON.parse(risk?.recommendation);
        const languageKey = i18n?.language?.toUpperCase();
        const localizedSubstance = parsed?.[languageKey]?.substanceName;

        if (localizedSubstance) {
          return localizedSubstance;
        }
      }
    } catch (_error) {
      // Keep fallback resolution below when recommendation is not JSON.
    }

    return risk?.active_substance || risk?.substanceKey || "-";
  };

  const getRiskRecommendationText = (risk) => {
    if (!risk?.recommendation) {
      return "-";
    }

    try {
      return buildInteractionParam(risk?.recommendation, true) || "-";
    } catch (_error) {
      return `${risk?.recommendation}`;
    }
  };

  const buildRiskChangeText = (drug) => {
    let relativeRisk = +drug?.ddgi?.relative_risk_change;

    if (!drug?.ddgi) {
      return t(Consts.NOT_AVAILABLE);
    }

    if (relativeRisk === -1) {
      return t("see_explanation");
    }

    if (relativeRisk === 0) {
      return t("is_not_increased");
    }

    relativeRisk = (Math.round(relativeRisk * 100 * 100) / 100).toFixed(2);
    const decrOrIncr = relativeRisk < 0 ? t("has_decreased_by") : t("has_increased_by");
    return `${decrOrIncr} ${Math.abs(relativeRisk)}%`;
  };

  const formatRiskPercentage = (riskValue) => {
    const numeric = +riskValue;

    if (numeric === -1) {
      return t("not_available");
    }

    if (numeric <= 0) {
      return t("no_risk");
    }

    return `${(Math.round(numeric * 100 * 100) / 100).toFixed(2)}%`;
  };

  const buildMedicationCell = (drug) => {
    const medicationName = drug?.productName === Consts.NOT_SPECIFIED || drug?.productName === Consts.ANY
      ? t(Consts.NOT_SPECIFIED)?.toLowerCase()
      : drug?.productName;

    const strengthPart = drug?.strength && +drug?.strength > 0
      ? ` ${drug?.strength}${drug?.strengthUnit ? ` ${drug?.strengthUnit}` : ""}`
      : "";

    return `${medicationName}${strengthPart} (${normalizeSubstance(drug?.activeSubstance)}), ${t("start_date")?.toLowerCase?.() || "start"}: ${formatDateTime(drug?.startDate, true)}`;
  };

  const buildDdiCell = (drug) => {
    if (!Array.isArray(drug?.ddis) || drug?.ddis?.length === 0) {
      return t("not_available");
    }

    return drug?.ddis
      ?.map((ddi, idx) => {
        const compound = ddi?.ddiDrug?.compound || ddi?.ddiDrug?.activeSubstance || t("not_specified");
        const product = ddi?.ddiDrug?.productName || t("not_specified");
        const relevanceKey = ddi?.relevance?.apiText;
        const localizedRelevance = ddiRelevances?.[relevanceKey]?.[i18n.language?.toUpperCase?.()] || relevanceKey || t("not_available");

        return `${idx + 1}. ${t("with_this_medicine")} ${compound} (in ${product}), ${t("relevance")}: ${localizedRelevance}`;
      })
      ?.join("\n");
  };

  const buildDgiCell = (drug) => {
    if (!Array.isArray(drug?.dgis) || drug?.dgis?.length === 0) {
      return t("not_available");
    }

    return drug?.dgis
      ?.map((dgi, idx) => {
        const phenotypeKey = dgi?.phenotype;
        const phenotypeText = phenotypeTypes?.[phenotypeKey]?.[i18n.language?.toUpperCase?.()] || phenotypeKey || t("not_available");
        const recommendation = dgi?.genotypeRecommendation
          || dgi?.phenotypeRecommendation
          || dgi?.completeRecommendation
          || dgi?.completpeRecommendation;
        const recommendationText = recommendation
          ? buildInteractionParam(recommendation, true)
          : t("not_available");

        return `${idx + 1}. ${t("with_this_medicine")} ${dgi?.gene || t("gene")}, ${t("genotype")}: ${dgi?.genotype || "-"}, ${t("phenotype")}: ${phenotypeText}, ${t("recommendation")}: ${recommendationText}`;
      })
      ?.join("\n");
  };

  const buildRiskCell = (drug, isPatientReport) => {
    if (isPatientReport) {
      return t("not_available");
    }

    if (!drug?.ddgi) {
      return t("not_available");
    }

    const riskWithout = formatRiskPercentage(drug?.ddgi?.baseline_risk);
    const riskWith = formatRiskPercentage(drug?.ddgi?.combined_risk);

    return `${buildRiskChangeText(drug)}\n${t("without_this_medicine")}: ${riskWithout}\n${t("with_this_medicine")}: ${riskWith}`;
  };

  const buildPrecisionDosingStatusText = (dosageReport) => {
    if (dosageReport?.reportAvailable) {
      return t("yes");
    }

    if (!dosageReport?.reportAvailable && dosageReport?.orderId && dosageReport?.message) {
      return t("requested");
    }

    if (!dosageReport?.reportAvailable && !dosageReport?.orderId && dosageReport?.message) {
      return dosageReport?.message === "no model found for this medicine"
        ? t("no_model_found")
        : t("not_available");
    }

    if (!dosageReport?.reportAvailable && !dosageReport?.message && dosageReport?.id) {
      return t("requested");
    }

    return t("not_requested");
  };

  const translateValue = (value) => {
    if (!value) {
      return "-";
    }

    const translated = t(value);
    return translated || value;
  };

  const addSectionTitle = (doc, y, title, fontFamily) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const maxTextWidth = pageWidth - PDF_LAYOUT.left - PDF_LAYOUT.right;

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(13);
    const titleLines = doc.splitTextToSize(title, maxTextWidth);
    const neededHeight = titleLines.length * 5;

    if (y + neededHeight > pageHeight - 16) {
      doc.addPage();
      return addSectionTitle(doc, PDF_LAYOUT.top, title, fontFamily);
    }

    doc.text(titleLines, PDF_LAYOUT.left, y);
    return y + neededHeight;
  };

  const drawPageDecorations = (doc, reportTitle, patientName, fontFamily) => {
    const pages = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    for (let page = 1; page <= pages; page += 1) {
      doc.setPage(page);
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(10);
      doc.text(reportTitle, PDF_LAYOUT.left, 12);
      doc.setFont(fontFamily, "normal");
      doc.setFontSize(8);
      doc.text(patientName?.toString(), PDF_LAYOUT.left, 17);
      doc.setDrawColor(220, 226, 235);
      doc.line(PDF_LAYOUT.left, 20, pageWidth - PDF_LAYOUT.right, 20);
      doc.text(`${page}/${pages}`, pageWidth / 2, pageHeight - 5, {
        align: "center",
      });
    }
  };

  const renderTable = (
    doc,
    y,
    head,
    body,
    columnStyles = {},
    fontFamily = "helvetica",
    tableOptions = {},
  ) => {
    const {
      styles: customStyles = {},
      headStyles: customHeadStyles = {},
      bodyStyles: customBodyStyles = {},
      ...otherTableOptions
    } = tableOptions;

    autoTable(doc, {
      startY: y,
      head,
      body,
      theme: "grid",
      tableWidth: getPdfContentWidth(doc),
      margin: {
        left: PDF_LAYOUT.left,
        right: PDF_LAYOUT.right,
        top: PDF_LAYOUT.top,
        bottom: PDF_LAYOUT.bottom,
      },
      styles: {
        font: fontFamily,
        fontSize: 8,
        cellPadding: 1.7,
        textColor: [33, 37, 41],
        overflow: "linebreak",
        cellWidth: "auto",
        minCellWidth: 12,
        valign: "top",
        ...customStyles,
      },
      headStyles: {
        font: fontFamily,
        fillColor: [57, 73, 171],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        ...customHeadStyles,
      },
      bodyStyles: {
        ...customBodyStyles,
      },
      rowPageBreak: "avoid",
      columnStyles,
      ...otherTableOptions,
    });

    return (doc.lastAutoTable?.finalY || y) + PDF_LAYOUT.sectionGap;
  };

  const buildFilename = (patient) => {
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(
      now.getDate(),
    ).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes(),
    ).padStart(2, "0")}`;

    return `mmc-report-${getPatientName(patient, disabledSeeNames)}_${timestamp}.pdf`;
  };

  const fetchReportData = useCallback(async (categorySet) => {
    const resolvedPatient = selectedPatient
      ? selectedPatient
      : await getDbUser(context?.token, patientId);

    const [drugs, diseasesRaw, genes, symptoms, labTests, measurements] = await Promise.all([
      categorySet.has("drugs") ? getDbDrugs(context?.token, patientId, true, true) : Promise.resolve([]),
      categorySet.has("diseases") ? getDbDiseases(context?.token, patientId) : Promise.resolve({ diseases: [] }),
      (categorySet.has("pgx") || categorySet.has("pgxRecommendations"))
        ? getDbGenes(context?.token, patientId)
        : Promise.resolve([]),
      categorySet.has("symptoms") ? getDbSymptoms(context?.token, patientId) : Promise.resolve([]),
      categorySet.has("labTests") ? getDbLabTests(context?.token, patientId) : Promise.resolve([]),
      categorySet.has("measurements") ? getDbMeasurements(context?.token, patientId) : Promise.resolve([]),
    ]);

    let dosageReportsByDrugId = {};

    if (
      categorySet.has("drugs")
      && context?.role !== Consts.PATIENT
      && Array.isArray(drugs)
      && drugs.length > 0
    ) {
      const reportEntries = await Promise.all(
        drugs?.map(async (drug) => {
          try {
            const report = await getDosageRecommendationReport(context?.token, drug?.id);
            return [drug?.id, report || null];
          } catch (_error) {
            return [drug?.id, null];
          }
        }),
      );

      dosageReportsByDrugId = Object.fromEntries(reportEntries);
    }

    return {
      patient: resolvedPatient,
      drugs: Array.isArray(drugs) ? drugs : [],
      diseases: Array.isArray(diseasesRaw?.diseases) ? diseasesRaw?.diseases : [],
      genes: Array.isArray(genes) ? genes : [],
      symptoms: Array.isArray(symptoms) ? symptoms : [],
      labTests: Array.isArray(labTests) ? labTests : [],
      dosageReportsByDrugId,
      measurements: Array.isArray(measurements?.measurements)
        ? measurements?.measurements
        : Array.isArray(measurements)
          ? measurements
          : [],
    };
  }, [
    context?.token,
    getDbDiseases,
    getDbDrugs,
    getDbGenes,
    getDbLabTests,
    getDbMeasurements,
    getDbSymptoms,
    getDbUser,
    getDosageRecommendationReport,
    patientId,
    selectedPatient,
  ]);

  const generateStructuredPdf = useCallback(async (reportData, categorySet) => {
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const fontFamily = await applyPdfUnicodeFont(doc);
    const patientName = getPatientName(reportData?.patient, disabledSeeNames);
    const reportTitle = `${t("report_for")} ${patientName}`;
    let y = PDF_LAYOUT.top;

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(15);
    doc.text(reportTitle, PDF_LAYOUT.left, y);
    y += 7;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(10);
    doc.text(`${t("date")}: ${formatDateTime(new Date().toISOString(), false)}`, PDF_LAYOUT.left, y);
    y += 8;

    y = addSectionTitle(doc, y, t("medical_data"), fontFamily);
    const medicalRows = [
      [t("year_of_birth"), formatValue(reportData?.patient?.year_of_birth)],
      [t("genotypic_sex"), translateValue(reportData?.patient?.gender)],
      [t("ethnicity"), translateValue(reportData?.patient?.ethnicity?.toLowerCase?.() || reportData?.patient?.ethnicity)],
      [t("highest_level_of_education"), translateValue(reportData?.patient?.education)],
      [t("smoking_habits"), translateValue(reportData?.patient?.smoking_habit)],
      [
        t("alcohol_consuming"),
        reportData?.patient?.drinking_habit
          ? `${translateValue(reportData?.patient?.drinking_habit)}${
            reportData?.patient?.drinking_frequency
              ? ` (${translateValue(reportData?.patient?.drinking_frequency)})`
              : ""
          }`
          : "-",
      ],
    ];
    y = renderTable(doc, y, [[t("category"), t("value")]], medicalRows, { 0: { cellWidth: 58 } }, fontFamily);

    if (categorySet.has("diseases")) {
      y = addSectionTitle(doc, y, t("diseases"), fontFamily);
      const diseaseRows = reportData?.diseases?.length > 0
        ? reportData?.diseases?.map((item) => [
          formatDateTime(item?.begin, true),
          formatValue(item?.icd10),
          formatValue(item?.icd10name),
          formatValue(item?.snomed),
        ])
        : [[{ content: t("not_available"), colSpan: 4, styles: { halign: "center" } }]];

      y = renderTable(
        doc,
        y,
        [[t("date"), t("icd10_code"), t("icd10_name"), t("snomed_code")]],
        diseaseRows,
        {
          0: { cellWidth: 24 },
          1: { cellWidth: 28 },
          2: { cellWidth: 90 },
          3: { cellWidth: 40 },
        },
        fontFamily,
      );
    }

    if (categorySet.has("pgx")) {
      y = addSectionTitle(doc, y, t("pharmacogenetic_test_results"), fontFamily);
      const geneRows = reportData?.genes?.length > 0
        ? reportData?.genes?.map((gene) => [
          formatDateTime(gene?.testDate, true),
          formatValue(gene?.geneName),
          formatValue(gene?.genotype),
          t(formatValue(gene?.phenotype)),
          formatValue(
            Array.isArray(gene?.risks)
              ? gene?.risks
                ?.map((risk) => getRiskSubstance(risk))
                .filter(Boolean)
                .join(", ")
              : "",
          ),
        ])
        : [[{ content: t("not_available"), colSpan: 5, styles: { halign: "center" } }]];

      y = renderTable(
        doc,
        y,
        [[t("test_date"), t("gene"), t("genotype"), t("phenotype"), t("critical_medicines_and_dpwg")]],
        geneRows,
        {
          0: { cellWidth: 24 },
          1: { cellWidth: 22 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 76 },
        },
        fontFamily,
        {
          styles: { overflow: "linebreak", cellWidth: "auto" },
          tableWidth: "auto",
        },
      );
    }

    if (categorySet.has("pgxRecommendations")) {
      y = addSectionTitle(doc, y, t("critical_medicines_and_dpwg"), fontFamily);
      const dgiRows = reportData?.genes?.length > 0
        ? reportData?.genes?.flatMap((gene) => {
          const risks = Array.isArray(gene?.risks) ? gene?.risks : [];

          if (risks?.length === 0) {
            return [[
              formatValue(gene?.geneName),
              translateValue(gene?.phenotype),
              "-",
              t("no_risk"),
            ]];
          }

          return risks?.map((risk) => [
            formatValue(gene?.geneName),
            translateValue(gene?.phenotype),
            getRiskSubstance(risk),
            getRiskRecommendationText(risk),
          ]);
        })
        : [[{ content: t("not_available"), colSpan: 4, styles: { halign: "center" } }]];

      y = renderTable(
        doc,
        y,
        [[t("gene"), t("phenotype"), t("active_substance"), t("recommendations_of_dpwg")]],
        dgiRows,
        { 3: { cellWidth: 88 } },
        fontFamily,
      );
    }

    if (categorySet.has("drugs")) {
      y = addSectionTitle(doc, y, t("safety_information_for_medication"), fontFamily);
      const isPatientReport = context?.role === Consts.PATIENT;
      const drugRows = reportData?.drugs?.length > 0
        ? reportData?.drugs?.map((drug) => {
          const dosageReport = reportData?.dosageReportsByDrugId?.[drug?.id];
          return [
            buildMedicationCell(drug),
            buildDdiCell(drug),
            buildDgiCell(drug),
            buildRiskCell(drug, isPatientReport),
            isPatientReport ? t("not_available") : buildPrecisionDosingStatusText(dosageReport),
          ];
        })
        : [[{ content: t("not_available"), colSpan: 5, styles: { halign: "center" } }]];

      y = renderTable(
        doc,
        y,
        [[
          t("medication"),
          t("drug_drug_interactions"),
          t("drug_gene_interactions"),
          t("adverse_event_risks"),
          t("dosage_recommendation"),
        ]],
        drugRows,
        {
          0: { cellWidth: 34 },
          1: { cellWidth: 37 },
          2: { cellWidth: 37 },
          3: { cellWidth: 37 },
          4: { cellWidth: 37 },
        },
        fontFamily,
      );
    }

    if (categorySet.has("symptoms")) {
      y = addSectionTitle(doc, y, t("symptoms"), fontFamily);
      const symptomRows = reportData?.symptoms?.length > 0
        ? reportData?.symptoms?.map((symptom) => [
          formatDateTime(symptom?.startDate, false),
          symptom?.endDate ? formatDateTime(symptom?.endDate, false) : "-",
          translateValue(symptom?.type),
          translateValue(symptom?.term),
          formatValue(symptom?.sufferingLevel),
          formatValue(symptom?.comment),
        ])
        : [[{ content: t("not_available"), colSpan: 6, styles: { halign: "center" } }]];

      y = renderTable(
        doc,
        y,
        [[t("start_date"), t("end_date"), t("category"), t("symptom"), t("severity"), t("comment")]],
        symptomRows,
        {},
        fontFamily,
      );
    }

    if (categorySet.has("labTests")) {
      y = addSectionTitle(doc, y, t("lab_tests"), fontFamily);
      const labRows = reportData?.labTests?.length > 0
        ? reportData?.labTests?.flatMap((test) => {
          const params = Array.isArray(test?.labParams) && test?.labParams?.length > 0
            ? test?.labParams
            : [{ parameterName: "-", value: "-", unit: "" }];

          return params?.map((param) => [
            formatDateTime(test?.testDate, true),
            translateValue(test?.labTestName),
            formatValue(param?.parameterName),
            `${formatValue(param?.value)} ${formatValue(param?.unit)}`,
          ]);
        })
        : [[{ content: t("not_available"), colSpan: 4, styles: { halign: "center" } }]];

      y = renderTable(doc, y, [[t("test_date"), t("test"), t("parameter"), t("value")]], labRows, {}, fontFamily);
    }

    if (categorySet.has("measurements")) {
      y = addSectionTitle(doc, y, t("measurements"), fontFamily);
      const measurementRows = reportData?.measurements?.length > 0
        ? reportData?.measurements?.flatMap((measurement) => {
          const params = Array.isArray(measurement?.params) && measurement?.params?.length > 0
            ? measurement?.params
            : [{ name: "-", value: "-", unit: "" }];

          return params?.map((param) => [
            formatDateTime(measurement?.dateTime, false),
            translateValue(measurement?.name),
            translateValue(param?.name),
            `${formatValue(param?.value)} ${translateValue(param?.unit)}`,
          ]);
        })
        : [[{ content: t("not_available"), colSpan: 4, styles: { halign: "center" } }]];

      renderTable(doc, y, [[t("measurement_date"), t("measurement_name"), t("parameter"), t("value")]], measurementRows, {}, fontFamily);
    }

    drawPageDecorations(doc, reportTitle, patientName, fontFamily);
    doc.save(buildFilename(reportData?.patient));
  }, [context?.role, disabledSeeNames, t]);

  const handlePrint = useCallback(async (forcedCategories = null) => {
    if (!patientId) {
      return;
    }

    const categoryIds = forcedCategories?.length
      ? forcedCategories
      : selectedItems?.length
        ? selectedItems
        : ALL_CATEGORY_IDS;

    const categorySet = new Set(categoryIds);
    setShowSelection(false);
    setIsPrinting(true);

    try {
      const reportData = await fetchReportData(categorySet);
      await generateStructuredPdf(reportData, categorySet);
    } finally {
      setIsPrinting(false);
      if (onFinishPrintReport) {
        onFinishPrintReport();
      }
    }
  }, [fetchReportData, generateStructuredPdf, onFinishPrintReport, patientId, selectedItems]);

  const handleCheckboxChange = (e, item) => {
    if (e.target.checked) {
      setSelectedItems((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
    } else {
      setSelectedItems((prev) =>
        prev.filter((id) => id !== item.id),
      );
    }
  };

  const printCategories = [
    { id: "diseases", name: t("diseases") },
    { id: "pgx", name: t("pgx_test") },
    { id: "pgxRecommendations", name: t("critical_medicines_and_dpwg") },
    { id: "drugs", name: t("ddi") },
    { id: "symptoms", name: t("symptoms") },
    { id: "labTests", name: t("lab_tests") },
    { id: "measurements", name: t("measurements") },
  ];

  return (
    <>
      {showSelection && (
        <div className="feedback-modal">
          <div
            className={`w3-modal-content feedback-modal-content dialog-draggable report-print-dialog ${isDragging ? "dialog-draggable--dragging" : ""}`}
            style={dragStyle || { transform: "translate(-50%, -50%)" }}
          >
            <header className="dialog-drag-header" onMouseDown={onDragHandleMouseDown}>
              <div className="dialog-drag-header__title">{t("select")}</div>
              <button
                type="button"
                className="dialog-drag-close"
                onClick={closeSelectionDialog}
                aria-label={t("close", { defaultValue: "Close" })}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="checkbox-group report-print-checkbox-group">
              {printCategories.map((item) => (
                <label key={item.id} className="checkbox-item report-print-checkbox-card">
                  <input
                    className="report-print-checkbox-input"
                    type="checkbox"
                    value={item.id}
                    checked={selectedItems?.includes(item.id)}
                    onChange={(e) => handleCheckboxChange(e, item)}
                  />
                  {item.name}
                </label>
              ))}
            </div>
            <div className="feedback-modal-buttons">
              <button
                type="button"
                onClick={() => handlePrint()}
                className="symptom-ui-button symptom-ui-button--solid-primary"
                disabled={isPrinting || selectedItems?.length === 0}
              >
                {t("export")}
              </button>
            </div>
          </div>
        </div>
      )}
      {!selectedPatient && context?.role === Consts.PATIENT && (
        <Tooltip title={t("export")}
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: "1.3rem",
              },
            },
          }}
        >
          <button
            className="w3-bar-item w3-btn w3-round-xlarge w3-theme-d5"
            onClick={() => setShowSelection(true)}
            disabled={isPrinting}
          >
            <FontAwesomeIcon icon={faPrint} />
          </button>
        </Tooltip>
      )}
    </>
  );
};

export default ReportPrint;
